import { useContext, FC } from "react";
import { useMachine } from "@xstate/react";
import { GetActivityTypes_activityTypes } from "api/MyLivestock/Activity/types/GetActivityTypes";
import { GetActivityTemplates_activityTemplates } from "api/MyLivestock/Activity/types/GetActivityTemplates";
import { GetAnimalTypes_animalTypes } from "api/MyLivestock/Animal/types/GetAnimalTypes";
import { Species } from "state";
import { importCustomWizardMachine } from "./state/importCustomWizardMachine";
import { CommonContext } from "config/commonProvider";
import { ACTIVITIES_IMPORT, ANIMALS_IMPORT } from "constants/Import";
import { Flex, Paper, Spacer, Title, Text } from "components";
import { ActivityTemplateIcon } from "components/Activity";
import { ImportWizardChooseColumns } from "./ImportWizardChooseColumns";
import { ImportWizardChooseFile } from "./ImportWizardChooseFile";
import { ImportWizardChooseTemplate } from "./ImportWizardChooseTemplate";
import { ImportWizardChooseType } from "./ImportWizardChooseType";
import { ImportWizardStages } from "./ImportWizardStages";
import { ImportWizardResult } from "./ImportWizardResult";
import styles from "./ImportCustomWizard.module.scss";
import { UploadCsvMutation } from "generated/graphql";
import { capitaliseFirstLetter } from "@breedr/utils";

export const ImportCustomWizard: FC = () => {
  const [state, send] = useMachine(importCustomWizardMachine);

  // 1. Choose type
  const handleSelectActivityType = (activityType: GetActivityTypes_activityTypes) =>
    send("SELECT_ACTIVITY_TYPE", { activityType });

  const handleSelectAnimalImport = (animalType: Species | GetAnimalTypes_animalTypes) =>
    send("SELECT_ANIMAL_TYPE", { animalType });

  const importType = state.context.activityTemplate?.id ? ACTIVITIES_IMPORT : ANIMALS_IMPORT;

  // 2. Choose template
  const handleResetActivityType = () => send("RESET_ACTIVITY_TYPE");

  const handleSelectActivityTemplate = (activityTemplate: GetActivityTemplates_activityTemplates) =>
    send("SELECT_ACTIVITY_TEMPLATE", { activityTemplate });

  // 3. Choose file
  const handleClickBackFromChooseFile = () =>
    state.context.animalType ? send("RESET_ANIMAL_TYPE") : send("RESET_ACTIVITY_TEMPLATE");

  const { showNotification } = useContext(CommonContext);

  const handleSetUploadedFile = (
    uploadedFile: NonNullable<UploadCsvMutation["uploadCsv"]>,
    uploadedFileName: string,
  ) => {
    const errors = uploadedFile?.errors;

    if (errors) {
      showNotification({
        variant: "error",
        message: errors[0]?.message ? capitaliseFirstLetter(errors[0].message) : "Something went wrong",
      });
    } else {
      send("SET_UPLOADED_FILE", { uploadedFile, uploadedFileName });
    }
  };

  const handleResetUploadedFile = () => send("RESET_UPLOADED_FILE");

  const handleProceedWithUploadedFile = () => send("PROCEED_WITH_UPLOADED_FILE");

  // 4. Choose columns
  const handleSetTaskId = (taskId: string) => send("SET_TASK_ID", { taskId });

  // 5. Result
  const handleRestart = () => send("RESTART");

  const handleSetTaskSuccess = () => send("SET_TASK_SUCCESS");

  return (
    <Flex item itemGutter>
      <Paper className={styles.import_custom_wizard}>
        <Spacer baselineHeight={5} />

        <Flex container containerJustifyContent="center">
          <Flex container containerAlignItems="center">
            {state.value === "chooseTemplate" && state.context.activityType?.image && (
              <ActivityTemplateIcon
                activityTemplateName={state.context.activityType.name}
                className={styles.import_custom_wizard__title_icon}
                size="medium"
                src={state.context.activityType?.image}
              />
            )}

            <Title tertiary>{state.context.title}</Title>
          </Flex>

          <Spacer baselineHeight={1} />

          <Text>{state.context.subtitle}</Text>
        </Flex>

        <Spacer baselineHeight={5} />

        <ImportWizardStages activeStepNumber={state.context.stageNumber} />

        <Spacer baselineHeight={4} />

        {state.value === "chooseType" && (
          <ImportWizardChooseType
            onSelectActivityType={handleSelectActivityType}
            onSelectAnimalImport={handleSelectAnimalImport}
          />
        )}

        {state.value === "chooseTemplate" && state.context.activityType && (
          <ImportWizardChooseTemplate
            activityTypeId={state.context.activityType.id}
            onClickBack={handleResetActivityType}
            onSelect={handleSelectActivityTemplate}
          />
        )}

        {state.value === "chooseFile" && (
          <ImportWizardChooseFile
            onClickBack={handleClickBackFromChooseFile}
            onClickNext={handleProceedWithUploadedFile}
            onResetFile={handleResetUploadedFile}
            onSetFile={handleSetUploadedFile}
            importType={importType}
          />
        )}

        {state.value === "chooseColumns" && state.context.uploadedFile && (
          <ImportWizardChooseColumns
            activityTemplate={state.context.activityTemplate}
            file={state.context.uploadedFile}
            importType={importType}
            onChangeActivityTemplate={handleSelectActivityTemplate}
            onClickBack={handleResetUploadedFile}
            onRestart={handleRestart}
            onSuccess={handleSetTaskId}
          />
        )}

        {state.value === "result" && state.context.uploadedFileName && state.context.taskId && (
          <ImportWizardResult
            fileName={state.context.uploadedFileName}
            importType={importType}
            onClickRestart={handleRestart}
            onSuccess={handleSetTaskSuccess}
            taskId={state.context.taskId}
          />
        )}

        <Spacer baselineHeight={4} />
      </Paper>
    </Flex>
  );
};
